import React, { Suspense } from "react";
import {
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from "react-router-dom";
import App from "../App";
import Spinner from "@components/common/Spinner";
import { Box } from "@chakra-ui/react";
import { theme } from "src/styles/theme";
import Home from "src/pages/Home";
import ErrorBoundary from "@components/common/ErrorBoundary";
import SearchClientsComponent from "src/pages/Sales/Steps/SearchClientsComponent";
import AccountsPhysicalComponents from "src/pages/Sales/Steps/CreateAccount/AccountsPhysicalComponents";
import AccountsLegalComponents from "src/pages/Sales/Steps/CreateAccount/AccountsLegalComponents";
import CreateMyOportunityComponent from "src/pages/Sales/Steps/Oportunity/CreateMyOportunityComponent";
import MyOportunityComponent from "src/pages/Sales/Steps/Oportunity/MyOportunityComponent";
import DetailOportunityComponent from "src/pages/Sales/Steps/Oportunity/DetailOportunityComponent";
import Bolsao from "src/pages/Bolsao";
import { cookieKeys, getCookie } from "@helpers/cookies";

// Importações lazy para melhorar o carregamento inicial
const Login = React.lazy(() => import("../pages/Login"));
const NeedHelp = React.lazy(() => import("src/pages/NeedHelp"));
const MyProfile = React.lazy(() => import("src/pages/MyProfile"));
const Enterprises = React.lazy(() => import("src/pages/Enterprises"));
const SuccessUpdatePassword = React.lazy(
  () => import("src/pages/MyProfile/components/SuccessUpdatePassword"),
);
const Sales = React.lazy(() => import("src/pages/Sales"));
const NeedHelpAuthorizated = React.lazy(
  () => import("src/pages/NeedHelpAuthorizated"),
);
const DetailEnterpriseComponent = React.lazy(
  () => import("src/pages/Enterprises/Steps/DetailEnterpriseComponent"),
);
const UnityComponent = React.lazy(
  () => import("src/pages/Enterprises/Steps/UnityComponent"),
);
const Commission = React.lazy(() => import("src/pages/Commission"));

export const useAuth = () => {
  const token = getCookie(cookieKeys.token);

  return !!token;
};

const ProtectedRoute = ({ children }: any) => {
  const auth = useAuth();
  return auth ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }: any) => {
  const auth = useAuth();
  return !auth ? children : <Navigate to="/" />;
};

const LoadingFallback = () => (
  <Box
    display={"flex"}
    pos={"relative"}
    flexDir={"column"}
    justifyContent={"center"}
    alignItems={"center"}
    p={8}
    left={{ lg: "230px" }}
    mt={{ base: "20px", md: "15px", lg: "0px" }}
    w={{ lg: "calc(100% - 230px)" }}
    overflowX={"auto"}
    bg={theme.disable100}
    h={"100%"}
  >
    <Spinner />
  </Box>
);
const LoadingFallbackPublic = () => (
  <Box
    display={"flex"}
    pos={"relative"}
    flexDir={"column"}
    justifyContent={"center"}
    alignItems={"center"}
    p={8}
    w={{ lg: "100%" }}
    bg={theme.disable100}
    h={"100%"}
  >
    <Spinner />
  </Box>
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<App />}>
      <Route path="*" element={<Navigate to="/" />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Home />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/myProfile"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <MyProfile />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/empreendimentos"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Enterprises />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/detalhesEmpreendimentos"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <DetailEnterpriseComponent />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/unidade"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <UnityComponent />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      {/* <Route
        path="/oportunidades"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Sales />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      /> */}
      {/* Rotas de Oportunidades/Sales */}
      <Route
        path="/oportunidades"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Sales />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SearchClientsComponent />
            </Suspense>
          }
        />
        <Route
          path="criar-conta/pessoa-fisica"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <AccountsPhysicalComponents />
            </Suspense>
          }
        />
        <Route
          path="criar-conta/pessoa-juridica"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <AccountsLegalComponents />
            </Suspense>
          }
        />
        <Route
          path="criar"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <CreateMyOportunityComponent />
            </Suspense>
          }
        />
        <Route
          path="bolsao-oportunidades"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <Bolsao />
            </Suspense>
          }
        />
        <Route
          path="minhas-oportunidades"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <MyOportunityComponent />
            </Suspense>
          }
        />
        <Route
          path="minhas-oportunidades/detalhes"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <DetailOportunityComponent />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="/comissoes"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <Commission />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/needHelpAuth"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallback />}>
                <NeedHelpAuthorizated />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallbackPublic />}>
                <Login />
              </Suspense>
            </ErrorBoundary>
          </PublicRoute>
        }
      />
      <Route
        path="/needHelp"
        element={
          <PublicRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallbackPublic />}>
                <NeedHelp />
              </Suspense>
            </ErrorBoundary>
          </PublicRoute>
        }
      />
      <Route
        path="/successUpdatePassword"
        element={
          <PublicRoute>
            <ErrorBoundary>
              <Suspense fallback={<LoadingFallbackPublic />}>
                <SuccessUpdatePassword />
              </Suspense>
            </ErrorBoundary>
          </PublicRoute>
        }
      />
    </Route>,
  ),
);
